import { z } from 'zod'
import {
  artigoResponseSchema,
  artigosResponseSchema,
  assetUploadUrlResponseSchema,
  createArtigoResponseSchema,
  createTagResponseSchema,
  dashboardDetailResponseSchema,
  dashboardUsuariosResponseSchema,
  dashboardsResponseSchema,
  deleteArtigoResponseSchema,
  deleteTagResponseSchema,
  dnsConfigResponseSchema,
  editorLinkResponseSchema,
  portalStripeResponseSchema,
  setDashboardPrincipalErrorSchema,
  setDashboardPrincipalResponseSchema,
  setDashboardPrincipalSchema,
  siteResponseSchema,
  sitesResponseSchema,
  tagDetailResponseSchema,
  tagsResponseSchema,
  updateArtigoResponseSchema,
  updateDashboardResponseSchema,
  updateTagResponseSchema,
  uploadUrlResponseSchema,
  mapCreatePortalStripeFormToApiRequest,
  mapCreateTagFormToApiRequest,
  mapUpdateTagFormToApiRequest,
  mapCreateArtigoFormToApiRequest,
  mapUpdateArtigoFormToApiRequest,
  mapUpdateSiteFormToApiRequest,
  mapUpdateDashboardFormToApiRequest,
  mapGenerateImageUploadFormToApiRequest,
  mapGenerateVideoUploadFormToApiRequest,
  mapGenerateAssetUploadFormToApiRequest
} from './workspace.schema'
import type {
  ArtigoResponse,
  ArtigosResponse,
  AssetUploadUrlResponse,
  CreateArtigoForm,
  CreateArtigoResponse,
  DeleteArtigoResponse,
  CreatePortalStripeForm,
  CreateTagForm,
  CreateTagResponse,
  DNSConfigResponse,
  DashboardDetailResponse,
  DashboardUsuariosResponse,
  DashboardsResponse,
  DeleteTagResponse,
  EditorLinkResponse,
  GenerateAssetUploadRequest,
  GenerateImageUploadRequest,
  GenerateVideoUploadRequest,
  PortalStripeResponse,
  SetDashboardPrincipalResponse,
  SiteResponse,
  SitesResponse,
  TagDetailResponse,
  TagsResponse,
  UpdateArtigoForm,
  UpdateArtigoResponse,
  UpdateDashboardForm,
  UpdateDashboardResponse,
  UpdateSiteForm,
  UpdateTagForm,
  UpdateTagResponse,
  UploadUrlResponse,
} from './workspace.schema'
import { apiClientJsonParsed } from '@/lib/api-client'
import { rethrowApiError } from '@/lib/api-error'

const setDashboardPrincipalResultSchema = z.union([
  setDashboardPrincipalResponseSchema,
  setDashboardPrincipalErrorSchema,
])

export async function getDashboards(): Promise<DashboardsResponse> {
  try {
    return await apiClientJsonParsed(
      '/api/v2/workspace/dashboard',
      dashboardsResponseSchema,
      { method: 'GET' },
    )
  } catch (error: any) {
    
    rethrowApiError(error, 'Erro ao buscar dashboards')
  }
}

export async function setDashboardPrincipal(
  dashboardId: number | null,
): Promise<SetDashboardPrincipalResponse> {
  try {
    const validatedRequest = setDashboardPrincipalSchema.parse({
      dashboardPrincipal: dashboardId === null ? null : String(dashboardId),
    })
    const result = await apiClientJsonParsed(
      '/api/v2/workspace/dashboard/principal',
      setDashboardPrincipalResultSchema,
      { method: 'PUT', body: JSON.stringify(validatedRequest) },
    )
    if (!result.success) {
      const errorData = setDashboardPrincipalErrorSchema.parse(result)
      rethrowApiError(errorData.error, 'Erro ao definir dashboard principal')
    }
    return setDashboardPrincipalResponseSchema.parse(result)
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao definir dashboard principal')
  }
}

export async function getDashboard(
  dashboardId: string | number,
): Promise<DashboardDetailResponse> {
  try {
    return await apiClientJsonParsed(
      `/api/v2/dashboard/${dashboardId}`,
      dashboardDetailResponseSchema,
      { method: 'GET' },
    )
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao buscar dashboard')
  }
}

export async function getSites(
  dashboardId: string | number,
): Promise<SitesResponse> {
  try {
    return await apiClientJsonParsed(
      `/api/v2/dashboard/${dashboardId}/sites`,
      sitesResponseSchema,
      { method: 'GET' },
    )
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao buscar sites')
  }
}

export async function getSite(
  dashboardId: string | number,
  siteId: string | number,
): Promise<SiteResponse> {
  try {
    return await apiClientJsonParsed(
      `/api/v2/dashboard/${dashboardId}/site/${siteId}`,
      siteResponseSchema,
      { method: 'GET' },
    )
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao buscar site')
  }
}

export async function updateSite(
  dashboardId: string | number,
  siteId: string | number,
  data: UpdateSiteForm,
): Promise<SiteResponse> {
  try {
    const validatedData = mapUpdateSiteFormToApiRequest(data)
    return await apiClientJsonParsed(
      `/api/v2/dashboard/${dashboardId}/site/${siteId}`,
      siteResponseSchema,
      { method: 'PUT', body: JSON.stringify(validatedData) },
    )
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao atualizar site')
  }
}

export async function getDNSConfig(
  dashboardId: string | number,
  siteId: string | number,
): Promise<DNSConfigResponse> {
  try {
    return await apiClientJsonParsed(
      `/api/v2/dashboard/${dashboardId}/site/${siteId}/dns-config`,
      dnsConfigResponseSchema,
      { method: 'GET' },
    )
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao buscar configuração DNS')
  }
}

export async function getArtigos(
  dashboardId: string | number,
  siteId: string | number,
  params?: {
    page?: number
    take?: number
    itemsPerPage?: number
    status?: string
    search?: string
    tags?: string
  },
): Promise<ArtigosResponse> {
  try {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.append('page', String(params.page))
    if (params?.take) searchParams.append('take', String(params.take))
    if (params?.itemsPerPage)
      searchParams.append('itemsPerPage', String(params.itemsPerPage))
    if (params?.status) searchParams.append('status', params.status)
    if (params?.tags) searchParams.append('tags', params.tags)
    if (params?.search) searchParams.append('search', params.search)
    const qs = searchParams.toString()
    const url = `/api/v2/dashboard/${dashboardId}/site/${siteId}/artigos${qs ? `?${qs}` : ''}`
    return await apiClientJsonParsed(url, artigosResponseSchema, { method: 'GET' })
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao buscar artigos')
  }
}

export async function getArtigo(
  dashboardId: string | number,
  siteId: string | number,
  artigoId: string | number,
): Promise<ArtigoResponse> {
  try {
    return await apiClientJsonParsed(
      `/api/v2/dashboard/${dashboardId}/site/${siteId}/artigo/${artigoId}`,
      artigoResponseSchema,
      { method: 'GET' },
    )
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao buscar artigo')
  }
}

export async function getTags(
  dashboardId: string | number,
  siteId: string | number,
): Promise<TagsResponse> {
  try {
    return await apiClientJsonParsed(
      `/api/v2/dashboard/${dashboardId}/site/${siteId}/tags`,
      tagsResponseSchema,
      { method: 'GET' },
    )
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao buscar tags')
  }
}

export async function getTag(
  dashboardId: string | number,
  siteId: string | number,
  tagId: string | number,
): Promise<TagDetailResponse> {
  try {
    return await apiClientJsonParsed(
      `/api/v2/dashboard/${dashboardId}/site/${siteId}/tag/${tagId}`,
      tagDetailResponseSchema,
      { method: 'GET' },
    )
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao buscar tag')
  }
}

export async function createTag(
  dashboardId: string | number,
  siteId: string | number,
  data: CreateTagForm,
): Promise<CreateTagResponse> {
  try {
    const validatedData = mapCreateTagFormToApiRequest(data)
    return await apiClientJsonParsed(
      `/api/v2/dashboard/${dashboardId}/site/${siteId}/tag`,
      createTagResponseSchema,
      { method: 'POST', body: JSON.stringify(validatedData) },
    )
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao criar tag')
  }
}

export async function updateTag(
  dashboardId: string | number,
  siteId: string | number,
  tagId: string | number,
  data: UpdateTagForm,
): Promise<UpdateTagResponse> {
  try {
    const validatedData = mapUpdateTagFormToApiRequest(data)
    return await apiClientJsonParsed(
      `/api/v2/dashboard/${dashboardId}/site/${siteId}/tag/${tagId}`,
      updateTagResponseSchema,
      { method: 'PUT', body: JSON.stringify(validatedData) },
    )
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao atualizar tag')
  }
}

export async function deleteTag(
  dashboardId: string | number,
  siteId: string | number,
  tagId: string | number,
): Promise<DeleteTagResponse> {
  try {
    return await apiClientJsonParsed(
      `/api/v2/dashboard/${dashboardId}/site/${siteId}/tag/${tagId}`,
      deleteTagResponseSchema,
      { method: 'DELETE', body: JSON.stringify({}) },
    )
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao deletar tag')
  }
}

export async function createArtigo(
  dashboardId: string | number,
  siteId: string | number,
  data: CreateArtigoForm,
): Promise<CreateArtigoResponse> {
  try {
    const validatedData = mapCreateArtigoFormToApiRequest(data)
    return await apiClientJsonParsed(
      `/api/v2/dashboard/${dashboardId}/site/${siteId}/artigo`,
      createArtigoResponseSchema,
      { method: 'POST', body: JSON.stringify(validatedData) },
    )
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao criar artigo')
  }
}

export async function updateArtigo(
  dashboardId: string | number,
  siteId: string | number,
  artigoId: string | number,
  data: UpdateArtigoForm,
): Promise<UpdateArtigoResponse> {
  try {
    const validatedData = mapUpdateArtigoFormToApiRequest(data)
    return await apiClientJsonParsed(
      `/api/v2/dashboard/${dashboardId}/site/${siteId}/artigo/${artigoId}`,
      updateArtigoResponseSchema,
      { method: 'PUT', body: JSON.stringify(validatedData) },
    )
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao atualizar artigo')
  }
}

export async function aprovarArtigo(
  dashboardId: string | number,
  siteId: string | number,
  artigoId: string | number,
): Promise<ArtigoResponse> {
  try {
    return await apiClientJsonParsed(
      `/api/v2/dashboard/${dashboardId}/site/${siteId}/artigo/${artigoId}/aprovar`,
      artigoResponseSchema,
      { method: 'POST', body: JSON.stringify({}) },
    )
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao aprovar artigo')
  }
}

export async function deleteArtigo(
  dashboardId: string | number,
  siteId: string | number,
  artigoId: string | number,
): Promise<DeleteArtigoResponse> {
  try {
    return await apiClientJsonParsed(
      `/api/v2/dashboard/${dashboardId}/site/${siteId}/artigo/${artigoId}`,
      deleteArtigoResponseSchema,
      { method: 'DELETE' },
    )
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao excluir artigo')
  }
}

export async function generateImageUploadUrl(
  dashboardId: string | number,
  siteId: string | number,
  data: GenerateImageUploadRequest,
): Promise<UploadUrlResponse> {
  try {
    const validatedData = mapGenerateImageUploadFormToApiRequest(data)
    return await apiClientJsonParsed(
      `/api/v2/dashboard/${dashboardId}/site/${siteId}/upload/image`,
      uploadUrlResponseSchema,
      { method: 'POST', body: JSON.stringify(validatedData) },
    )
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao gerar URL de upload de imagem')
  }
}

export async function generateVideoUploadUrl(
  dashboardId: string | number,
  siteId: string | number,
  data: GenerateVideoUploadRequest,
): Promise<UploadUrlResponse> {
  try {
    const validatedData = mapGenerateVideoUploadFormToApiRequest(data)
    return await apiClientJsonParsed(
      `/api/v2/dashboard/${dashboardId}/site/${siteId}/upload/video`,
      uploadUrlResponseSchema,
      { method: 'POST', body: JSON.stringify(validatedData) },
    )
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao gerar URL de upload de vídeo')
  }
}

export async function getEditorLink(
  dashboardId: string | number,
  siteId: string | number,
): Promise<EditorLinkResponse> {
  try {
    return await apiClientJsonParsed(
      `/api/v2/dashboard/${dashboardId}/site/${siteId}/editor-link`,
      editorLinkResponseSchema,
      { method: 'GET' },
    )
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao gerar link do editor')
  }
}

export async function uploadFileToS3(
  uploadUrl: string,
  file: File,
  fileType: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', uploadUrl)
    xhr.setRequestHeader('Content-Type', fileType)
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve()
      else {
        reject({
          message: `Erro no upload do arquivo ao S3: ${xhr.status} ${xhr.statusText}`,
          statusCode: xhr.status,
        })
      }
    }
    xhr.onerror = () => {
      reject({ message: 'Erro de rede no upload ao S3', statusCode: 0 })
    }
    xhr.send(file)
  })
}

export async function getDashboardUsuarios(
  dashboardId: string | number,
): Promise<DashboardUsuariosResponse> {
  try {
    return await apiClientJsonParsed(
      `/api/v2/dashboard/${dashboardId}/usuarios`,
      dashboardUsuariosResponseSchema,
      { method: 'GET' },
    )
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao buscar usuários da dashboard')
  }
}

export async function updateDashboard(
  dashboardId: string | number,
  data: UpdateDashboardForm,
): Promise<UpdateDashboardResponse> {
  try {
    const validatedRequest = mapUpdateDashboardFormToApiRequest(data)
    return await apiClientJsonParsed(
      `/api/v2/dashboard/${dashboardId}`,
      updateDashboardResponseSchema,
      { method: 'PUT', body: JSON.stringify(validatedRequest) },
    )
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao atualizar dashboard')
  }
}

export async function generateAssetUploadUrl(
  data: GenerateAssetUploadRequest,
): Promise<AssetUploadUrlResponse> {
  try {
    const validatedData = mapGenerateAssetUploadFormToApiRequest(data)
    return await apiClientJsonParsed(
      '/api/v2/upload/presigned-url',
      assetUploadUrlResponseSchema,
      { method: 'POST', body: JSON.stringify(validatedData) },
    )
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao gerar URL de upload de asset')
  }
}

export async function createPortalStripe(
  dashboardId: string | number,
  data?: CreatePortalStripeForm,
): Promise<PortalStripeResponse> {
  try {
    const requestBody = data
      ? mapCreatePortalStripeFormToApiRequest(data)
      : undefined
    return await apiClientJsonParsed(
      `/api/v2/dashboard/${dashboardId}/portal-stripe`,
      portalStripeResponseSchema,
      {
        method: 'POST',
        body: requestBody ? JSON.stringify(requestBody) : undefined,
      },
    )
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao gerar URL do portal Stripe')
  }
}
